import { clsx } from 'clsx';
import get from 'lodash/fp/get';
import { ChevronDown, ChevronRight } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import Loader from '../Loader';
import Filter from './Filter';
import Sorter from './Sorter';
import type { ExpandIconProps, SortOrder, TableProps } from './types';
import { SortingOrder } from './types';

// Default expand icon component
const DefaultExpandIcon = <Item,>({ expanded, onExpand, record }: ExpandIconProps<Item>) => (
    <button
        type="button"
        onClick={e => {
            e.stopPropagation();
            onExpand(record);
        }}
        className="flex h-5 w-5 items-center justify-center text-gray-500 hover:text-gray-700 focus:outline-none"
    >
        {expanded ? (
            <ChevronDown className="size-4" strokeWidth={2} />
        ) : (
            <ChevronRight className="size-4" strokeWidth={2} />
        )}
    </button>
);

const Table = <Item = any, ChildItem = Item>({
    columns,
    dataSource,
    rowKey = 'id',
    striped,
    equalWidth = false,
    containerClassName,
    tableClassName,
    onSortChange,
    onFilterChange,
    onRowClick,
    cellClassName,
    rowClassName,
    headCellClassName,
    loading,
    expandable,
}: TableProps<Item, ChildItem>) => {
    // State to track expanded rows
    const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

    const onSortOrderChange = useCallback(
        (columnKey: string, currentSortOrder?: SortOrder) => {
            let next: SortOrder = SortingOrder.Asc;

            if (currentSortOrder === SortingOrder.Asc) {
                next = SortingOrder.Desc;
            } else if (currentSortOrder === SortingOrder.Desc) {
                next = null;
            }

            if (onSortChange) {
                onSortChange(columnKey, next);
            }
        },
        [onSortChange]
    );

    const handleFilterChange = useCallback(
        (columnKey: string, value: string) => {
            if (onFilterChange) {
                onFilterChange(columnKey, value);
            }
        },
        [onFilterChange]
    );

    // Handle row expansion
    const handleExpand = useCallback(
        (record: Item) => {
            const recordKey = typeof rowKey === 'function' ? rowKey(record) : get(rowKey, record)?.toString();

            if (recordKey) {
                setExpandedRows(prev => {
                    const newState = { ...prev };
                    newState[recordKey] = !prev[recordKey];

                    // Call onExpand callback if provided
                    expandable?.onExpand?.(!prev[recordKey], record);

                    return newState;
                });
            }
        },
        [expandable, rowKey]
    );

    // Check if a row is expandable
    const isRowExpandable = useCallback(
        (record: Item) => {
            if (!expandable) {
                return false;
            }

            // If childrenField is specified, check if the record has children
            if (expandable.childrenField) {
                const children = get(expandable.childrenField, record);
                return Array.isArray(children) && children.length > 0;
            }

            // Otherwise, use the rowExpandable function if provided, or default to true
            return expandable.rowExpandable ? expandable.rowExpandable(record) : true;
        },
        [expandable]
    );

    // Get the expand icon component
    const ExpandIcon = expandable?.expandIcon || DefaultExpandIcon;

    // Build child column groupings aligned to parent columns
    const childGroups = useMemo(() => {
        if (!expandable) {
            return [] as NonNullable<typeof expandable>['childColumns'][];
        }

        const parentCols = columns;
        const childCols = expandable.childColumns || columns;

        const parentKey = (col: any) => (col.slotKey as string) || (col.key as string);
        const childByKey: Record<string, any> = {};
        childCols.forEach(col => {
            const key = (col.slotKey as string) || (col.key as string);
            if (key) {
                childByKey[key] = col;
            }
        });

        const groups: any[][] = parentCols.map(() => []);

        // If explicit childColumnGroups mapping is provided, honor it
        const explicitGroups = (expandable as any).childColumnGroups as Record<string, string[]> | undefined;
        if (explicitGroups) {
            parentCols.forEach((pCol, idx) => {
                const key = parentKey(pCol);
                const childKeys = explicitGroups[key] || [];
                const mapped = childKeys.map(k => childByKey[k]).filter(Boolean);
                groups[idx] = mapped;
            });
            return groups;
        }

        // Otherwise, default: match by slotKey/key 1:1, extras pack into last matching or remain empty
        const parentKeyToIndex: Record<string, number> = {};
        parentCols.forEach((pCol, idx) => {
            const k = parentKey(pCol);
            if (k) {
                parentKeyToIndex[k] = idx;
            }
        });

        childCols.forEach(cCol => {
            const k = (cCol.slotKey as string) || (cCol.key as string);
            const idx = k && parentKeyToIndex[k] !== undefined ? parentKeyToIndex[k] : undefined;
            if (idx !== undefined) {
                groups[idx].push(cCol);
            } else {
                // If no matching parent, append to the column before the last one if exists, else first
                const fallbackIdx = Math.max(0, parentCols.length - 2);
                groups[fallbackIdx]?.push(cCol);
            }
        });

        // Ensure at least one placeholder per empty group to keep structure if we have explicit child columns
        return groups;
    }, [expandable, columns]);

    // Render expanded content for a record
    const renderExpandedContent = useCallback(
        (record: Item, parentRowId: string) => {
            if (!expandable) {
                return null;
            }

            // If custom expandedRowRender is provided, use it
            if (expandable.expandedRowRender) {
                const content = expandable.expandedRowRender(record);
                return React.isValidElement(content)
                    ? React.cloneElement(content, { key: `expanded-${parentRowId}` })
                    : content;
            }

            // If childrenField is specified, render children as rows
            if (expandable.childrenField) {
                const children = get(expandable.childrenField, record) as ChildItem[];
                if (!Array.isArray(children) || children.length === 0) {
                    return null;
                }

                return (
                    <>
                        {expandable.showChildHeaders && (
                            <tr className="bg-neutral-50">
                                {/* empty expand column aligned with parent expand control and carrying border */}
                                <th className="w-10 border-t-0 border-b border-neutral-500 px-3 py-4" />
                                {columns.map((pCol, pIdx) => {
                                    const grouped = childGroups[pIdx] || [];
                                    return (
                                        <th
                                            key={`child-header-slot-${pIdx}`}
                                            className={clsx(
                                                'min-w-0 border-t-0 border-b border-neutral-500 px-3 py-4 text-left text-base leading-6 font-semibold text-neutral-900',
                                                headCellClassName ? headCellClassName(pIdx) : ''
                                            )}
                                            scope="col"
                                        >
                                            {grouped.length <= 1 ? (
                                                <div className="h-full w-full">{grouped[0]?.title || null}</div>
                                            ) : (
                                                <div
                                                    className={clsx('grid gap-2')}
                                                    style={{
                                                        gridTemplateColumns: `repeat(${grouped.length}, minmax(0, 1fr))`,
                                                    }}
                                                >
                                                    {grouped.map((gCol, gi) => (
                                                        <div key={`gh-${pIdx}-${gi}`}>{gCol.title}</div>
                                                    ))}
                                                </div>
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        )}
                        {children.map((child, childIndex) => {
                            const childKey =
                                typeof rowKey === 'function'
                                    ? rowKey(child as unknown as Item)
                                    : get(rowKey, child)?.toString() || `${parentRowId}-child-${childIndex}`;

                            if (expandable.childRowRender) {
                                const childRow = expandable.childRowRender(child, record);
                                return React.isValidElement(childRow)
                                    ? React.cloneElement(childRow, { key: childKey })
                                    : childRow;
                            }

                            return (
                                <tr
                                    key={childKey}
                                    className={clsx(
                                        'border-b border-neutral-300 bg-neutral-50',
                                        rowClassName?.(childIndex, child as unknown as Item)
                                    )}
                                >
                                    {/* empty expand column to align with parent expand control */}
                                    <td className="w-10 px-3 py-4" />
                                    {columns.map((pCol, pIdx) => {
                                        const grouped = childGroups[pIdx] || [];
                                        return (
                                            <td
                                                key={`${childKey}-slot-${pIdx}`}
                                                className={clsx(
                                                    'px-3 py-4 text-left text-base leading-normal font-medium text-neutral-500'
                                                )}
                                            >
                                                {grouped.length <= 1 ? (
                                                    <div
                                                        className={clsx(
                                                            'h-full w-full',
                                                            cellClassName && cellClassName(pIdx, childIndex)
                                                        )}
                                                    >
                                                        {grouped[0]
                                                            ? grouped[0].render
                                                                ? grouped[0].render(
                                                                      get(grouped[0].dataIndex, child),
                                                                      child as any
                                                                  )
                                                                : get(grouped[0].dataIndex, child)?.toString()
                                                            : null}
                                                    </div>
                                                ) : (
                                                    <div
                                                        className={clsx('grid items-center gap-2')}
                                                        style={{
                                                            gridTemplateColumns: `repeat(${grouped.length}, minmax(0, 1fr))`,
                                                        }}
                                                    >
                                                        {grouped.map((gCol, gi) => (
                                                            <div
                                                                key={`${childKey}-gc-${pIdx}-${gi}`}
                                                                className={clsx('min-w-0')}
                                                            >
                                                                {gCol.render
                                                                    ? gCol.render(
                                                                          get(gCol.dataIndex, child),
                                                                          child as any
                                                                      )
                                                                    : get(gCol.dataIndex, child)?.toString()}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </>
                );
            }

            return null;
        },
        [expandable, columns, rowKey, rowClassName, cellClassName, childGroups, headCellClassName]
    );

    return (
        <div className={clsx('w-full overflow-hidden rounded-lg', containerClassName)}>
            <div className="w-full overflow-x-auto">
                <table className={clsx('min-w-full', equalWidth ? 'table-fixed' : 'table-auto', tableClassName)}>
                    {equalWidth && (
                        <colgroup>
                            {Array.from({ length: columns.length + (expandable ? 1 : 0) }).map((_, i) => {
                                if (expandable && i === 0) {
                                    return <col key={i} style={{ width: '2.5rem' }} />;
                                }
                                const percent = columns.length === 0 ? 100 : 100 / columns.length;
                                return <col key={i} style={{ width: `${percent}%` }} />;
                            })}
                        </colgroup>
                    )}
                    <thead>
                        <tr className="rounded-tl-[10px] rounded-tr-[10px]">
                            {/* Add expand column if expandable is provided */}
                            {expandable && <th className="w-10 border-b border-neutral-500 px-3 py-4" />}
                            {columns.map((i, colIndex) => (
                                <th
                                    key={i.key || i.dataIndex.toString() || colIndex.toString()}
                                    className={clsx(
                                        // Allow header labels to wrap and share equal width
                                        'min-w-0 px-3 py-4 text-left text-base leading-normal font-semibold break-words whitespace-normal text-neutral-900',
                                        i.className && i.className,
                                        headCellClassName ? headCellClassName(colIndex) : 'border-b border-neutral-500'
                                    )}
                                    scope="col"
                                >
                                    <div className={clsx('h-full w-full')}>
                                        {!i.sorter && !i.filter && i.title}
                                        {i.sorter && !i.filter && (
                                            <Sorter column={i} onSortOrderChange={onSortOrderChange} />
                                        )}
                                        {i.filter && <Filter column={i} onFilterChange={handleFilterChange} />}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={expandable ? columns.length + 1 : columns.length}>
                                    <Loader />
                                </td>
                            </tr>
                        )}
                        {dataSource?.map((data, rowIndex) => {
                            const rowId =
                                typeof rowKey === 'function'
                                    ? rowKey(data)
                                    : get(rowKey, data)?.toString() || rowIndex.toString();
                            const isExpanded = expandedRows[rowId] || false;
                            const canExpand = isRowExpandable(data);

                            // Render the main row and its expanded content if any
                            return (
                                <React.Fragment key={`row-${rowId}`}>
                                    <tr
                                        className={clsx(
                                            'bg-white',
                                            !isExpanded && 'border-b border-neutral-300',
                                            isExpanded && 'border-b-0',
                                            striped && 'even:bg-gray-50',
                                            rowClassName?.(rowIndex, data)
                                        )}
                                        data-cy="tableRow"
                                        onClick={() => onRowClick && onRowClick(data)}
                                    >
                                        {/* Expand column */}
                                        {expandable && (
                                            <td className="w-10 px-3 py-4">
                                                {canExpand && (
                                                    <ExpandIcon
                                                        expanded={isExpanded}
                                                        onExpand={handleExpand}
                                                        record={data}
                                                    />
                                                )}
                                            </td>
                                        )}

                                        {columns.map((column, colIndex) => (
                                            <td
                                                key={`${rowId}-col-${column.key || colIndex}`}
                                                className={clsx(
                                                    'px-3 py-4 text-left text-base leading-normal font-medium whitespace-nowrap',
                                                    colIndex === 0 ? 'text-neutral-900' : 'text-neutral-500',
                                                    onRowClick && 'cursor-pointer',
                                                    column.className && column.className
                                                )}
                                            >
                                                <div
                                                    className={clsx(
                                                        'h-full w-full',
                                                        cellClassName && cellClassName(colIndex, rowIndex)
                                                    )}
                                                >
                                                    {column.render && column.render(get(column.dataIndex, data), data)}
                                                    {!column.render && get(column.dataIndex, data)?.toString()}
                                                </div>
                                            </td>
                                        ))}
                                    </tr>

                                    {/* Expanded content */}
                                    {expandable && isExpanded && renderExpandedContent(data, rowId)}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Table;
